import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { MatSnackBar, MatSnackBarVerticalPosition } from '@angular/material/snack-bar'
import { Observable, throwError } from 'rxjs'
import { catchError, timeout } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import { URLPaths } from '../utils/constants.util'
import { Router } from '@angular/router'
import { LocationModel } from '../ngrx-store/app.state'
import { PpaDeletionCategory } from '../utils/ppa-deletion.util'

export const ADMIN_STORAGE_KEY = 'corpers-admin-key'

/**
 * Admin API service. Does not extend BaseService to avoid circular dependency:
 * BaseService -> dialog components -> AdminService -> BaseService.
 */
/**
 * The admin post lists' filter for posts their poster deleted (kept, hidden, until they're removed
 * for good 14 days on): left out, shown too, or shown alone.
 */
export type DeletedPostsFilter = 'hide' | 'include' | 'only'

/** A PPA as GET /admin/ppas lists it. The deletion fields are set while it's deleted (and not yet removed for good). */
export interface AdminPpa {
  id: number
  slug_id: string
  name: string
  type_of_ppa: string
  created_at: string
  review_count: number
  corper_count: number
  Locations?: LocationModel[]
  deleted_at: string | null
  deletion_category: PpaDeletionCategory | null
  /** The admin's note. */
  deletion_reason: string | null
  DeletedBy: { id: number; username: string } | null
  /** Set on a merged duplicate: where its reviews and corp members went. */
  MergedInto: { id: number; slug_id: string; name: string } | null
  /** When it's removed for good, with its reviews. */
  purges_at: string | null
}

export interface BlockedPpaName {
  id: number
  name: string
  reason: string | null
  created_at: string
  BlockedBy: { id: number; username: string } | null
}

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  showNotification(
    message: string,
    duration = 5000,
    action = 'OK',
    verticalPosition: MatSnackBarVerticalPosition = 'bottom'
  ): void {
    this.snackBar.open(message, action, {
      duration,
      verticalPosition,
    })
  }

  get adminKey(): string | null {
    return sessionStorage.getItem(ADMIN_STORAGE_KEY)
  }

  setAdminKey(key: string): void {
    sessionStorage.setItem(ADMIN_STORAGE_KEY, key)
  }

  clearAdminKey(): void {
    sessionStorage.removeItem(ADMIN_STORAGE_KEY)
  }

  get isAdminLoggedIn(): boolean {
    return !!this.adminKey
  }

  private getAdminHeaders(): HttpHeaders {
    const token = this.adminKey
    return new HttpHeaders({
      'Content-Type': 'application/json',
      [environment.ADMIN_HEADER_KEY]: environment.ADMIN_HEADER_VALUE,
      ...(token && { Authorization: `Bearer ${token}` }),
    })
  }

  private getAdminFormHeaders(): HttpHeaders {
    const token = this.adminKey
    return new HttpHeaders({
      [environment.ADMIN_HEADER_KEY]: environment.ADMIN_HEADER_VALUE,
      ...(token && { Authorization: `Bearer ${token}` }),
    })
  }

  private handleError(error: HttpErrorResponse) {
    console.error('Admin API error', error)
    return throwError(error)
  }

  private buildAdminListUrl(
    basePath: string,
    params?: {
      page?: number
      limit?: number
      search?: string
      state_code?: string
      state?: string
      lga_id?: string
      lga?: string
      deleted?: DeletedPostsFilter
    }
  ): string {
    let url = environment.baseurl + basePath
    if (!params) return url

    const q = new URLSearchParams()
    if (params.page != null) q.set('page', String(params.page))
    if (params.limit != null) q.set('limit', String(params.limit))
    if (params.search) q.set('search', params.search)
    if (params.state_code) q.set('state_code', params.state_code)
    if (params.state) q.set('state', params.state)
    if (params.deleted && params.deleted !== 'hide') q.set('deleted', params.deleted)

    // API docs expose lga_id. Accept "lga" from UI and map it through for convenience.
    const lgaId = params.lga_id ?? params.lga
    if (lgaId) q.set('lga_id', lgaId)

    const qs = q.toString()
    if (qs) url += '?' + qs
    return url
  }

  logout(): void {
    this.clearAdminKey()
    this.router.navigate(['/admin/login'])
  }

  /**
   * Admin login: POST username and password as JSON, with x-admin-key from environment.
   * On success, the API returns a token; it is stored and sent as Authorization: Bearer <token> on subsequent requests.
   */
  login(body: { username: string; password: string }): Observable<any> {
    const url = environment.baseurl + URLPaths.adminLogin
    return this.http
      .post(url, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json', [environment.ADMIN_HEADER_KEY]: environment.ADMIN_HEADER_VALUE }),
        observe: 'response',
        responseType: 'json',
        withCredentials: true,
      })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  // --- Users ---
  getUsers(params?: {
    page?: number
    limit?: number
    search?: string
    state_code?: string
    state?: string
    lga_id?: string
    lga?: string
    deleted?: DeletedPostsFilter
  }): Observable<any> {
    const url = this.buildAdminListUrl(URLPaths.adminUsers, params)
    return this.http
      .get(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  deleteUser(id: number | string, reason?: string): Observable<any> {
    const url = environment.baseurl + URLPaths.adminUsersId.replace(':id', String(id))
    return this.http
      .delete(url, {
        body: reason ? { reason } : {},
        headers: this.getAdminHeaders(),
        observe: 'response',
        responseType: 'json',
        withCredentials: true,
      })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  blockUser(body: { email?: string; state_code?: string; reason?: string }): Observable<any> {
    const url = environment.baseurl + URLPaths.adminUsersBlock
    return this.http
      .post(url, body, {
        headers: this.getAdminHeaders(),
        observe: 'response',
        responseType: 'json',
        withCredentials: true,
      })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  unblockUser(id: number | string): Observable<any> {
    const url = environment.baseurl + URLPaths.adminUsersBlockId.replace(':id', String(id))
    return this.http
      .delete(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  getBlockedUsers(params?: { page?: number; limit?: number }): Observable<any> {
    let url = environment.baseurl + URLPaths.adminUsersBlocked
    if (params) {
      const q = new URLSearchParams()
      if (params.page != null) q.set('page', String(params.page))
      if (params.limit != null) q.set('limit', String(params.limit))
      const qs = q.toString()
      if (qs) url += '?' + qs
    }
    return this.http
      .get(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  getDeletedUsers(params?: { page?: number; limit?: number; search?: string }): Observable<any> {
    let url = environment.baseurl + URLPaths.adminUsersDeleted
    if (params) {
      const q = new URLSearchParams()
      if (params.page != null) q.set('page', String(params.page))
      if (params.limit != null) q.set('limit', String(params.limit))
      if (params.search) q.set('search', params.search)
      const qs = q.toString()
      if (qs) url += '?' + qs
    }
    return this.http
      .get(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  // --- Referrals ---
  getReferralLeaderboard(params?: { page?: number; limit?: number; search?: string }): Observable<any> {
    let url = environment.baseurl + URLPaths.adminReferralsLeaderboard
    if (params) {
      const q = new URLSearchParams()
      if (params.page != null) q.set('page', String(params.page))
      if (params.limit != null) q.set('limit', String(params.limit))
      if (params.search) q.set('search', params.search)
      const qs = q.toString()
      if (qs) url += '?' + qs
    }
    return this.http
      .get(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  // --- Posts ---
  deleteSalePost(id: number | string): Observable<any> {
    const url = environment.baseurl + URLPaths.adminPostsSaleId.replace(':id', String(id))
    return this.http
      .delete(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  deleteAccommodationPost(id: number | string): Observable<any> {
    const url = environment.baseurl + URLPaths.adminPostsAccommodationId.replace(':id', String(id))
    return this.http
      .delete(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  createSalePost(formData: FormData): Observable<any> {
    const url = environment.baseurl + URLPaths.adminPostsSale
    return this.http
      .post(url, formData, {
        headers: this.getAdminFormHeaders(),
        observe: 'response',
        responseType: 'json',
        withCredentials: true,
      })
      .pipe(timeout(60000), catchError(this.handleError))
  }

  createAccommodationPost(formData: FormData): Observable<any> {
    const url = environment.baseurl + URLPaths.adminPostsAccommodation
    return this.http
      .post(url, formData, {
        headers: this.getAdminFormHeaders(),
        observe: 'response',
        responseType: 'json',
        withCredentials: true,
      })
      .pipe(timeout(60000), catchError(this.handleError))
  }

  getSalePosts(params?: {
    page?: number
    limit?: number
    search?: string
    state_code?: string
    state?: string
    lga_id?: string
    lga?: string
    deleted?: DeletedPostsFilter
  }): Observable<any> {
    const url = this.buildAdminListUrl(URLPaths.adminPostsSale, params)
    return this.http
      .get(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  getAccommodationPosts(params?: {
    page?: number
    limit?: number
    search?: string
    state_code?: string
    state?: string
    lga_id?: string
    lga?: string
    deleted?: DeletedPostsFilter
  }): Observable<any> {
    const url = this.buildAdminListUrl(URLPaths.adminPostsAccommodation, params)
    return this.http
      .get(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  // --- PPAs ---
  /** `deleted: 'only'` is "Recently deleted" (with `category_counts`), newest first; `category` filters it. */
  getPpas(params: { page?: number; limit?: number; search?: string; deleted?: DeletedPostsFilter; category?: string }): Observable<any> {
    let url = this.buildAdminListUrl(URLPaths.adminPpas, params)
    if (params.category) url += (url.includes('?') ? '&' : '?') + 'category=' + encodeURIComponent(params.category)
    return this.http
      .get(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  /** Listed PPAs in the same LGA with a similar name - who to merge a duplicate into. */
  getSimilarPpas(id: number): Observable<any> {
    const url = environment.baseurl + URLPaths.adminPpasIdSimilar.replace(':id', String(id))
    return this.http
      .get(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  /** Soft deletes a PPA. The backend refuses it without a category, or without a note for "other". */
  deletePpa(id: number, body: { category: PpaDeletionCategory; note?: string; block_name?: boolean }): Observable<any> {
    const url = environment.baseurl + URLPaths.adminPpasId.replace(':id', String(id))
    return this.http
      .delete(url, { body, headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  /** Moves a duplicate's reviews, corp members and saves to `into_id`, then deletes it. */
  mergePpa(id: number, body: { into_id: number; note?: string }): Observable<any> {
    const url = environment.baseurl + URLPaths.adminPpasIdMerge.replace(':id', String(id))
    return this.http
      .post(url, body, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  /** `force`: restore it even though a PPA with the same name is listed there now (a 409 RESTORE_CONFLICT without it). */
  restorePpa(id: number, force = false): Observable<any> {
    const url = environment.baseurl + URLPaths.adminPpasIdRestore.replace(':id', String(id))
    return this.http
      .post(url, force ? { force: true } : {}, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  getBlockedPpaNames(params: { page?: number; limit?: number; search?: string }): Observable<any> {
    const url = this.buildAdminListUrl(URLPaths.adminPpasBlockedNames, params)
    return this.http
      .get(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  unblockPpaName(id: number): Observable<any> {
    const url = environment.baseurl + URLPaths.adminPpasBlockedNamesId.replace(':id', String(id))
    return this.http
      .delete(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  // --- Chat (national) ---
  deleteNationalMessage(id: number | string): Observable<any> {
    const url = environment.baseurl + URLPaths.adminChatsNationalMessagesId.replace(':id', String(id))
    return this.http
      .delete(url, { headers: this.getAdminHeaders(), observe: 'response', responseType: 'json', withCredentials: true })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  deleteNationalMessages(body?: { length?: number; filter?: any }): Observable<any> {
    const url = environment.baseurl + URLPaths.adminChatsNationalMessages
    return this.http
      .delete(url, {
        body: body ?? {},
        headers: this.getAdminHeaders(),
        observe: 'response',
        responseType: 'json',
        withCredentials: true,
      })
      .pipe(timeout(15000), catchError(this.handleError))
  }

  announceNational(body: { message?: string; title?: string }): Observable<any> {
    const url = environment.baseurl + URLPaths.adminChatsNationalAnnounce
    return this.http
      .post(url, body, {
        headers: this.getAdminHeaders(),
        observe: 'response',
        responseType: 'json',
        withCredentials: true,
      })
      .pipe(timeout(15000), catchError(this.handleError))
  }
}
